import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Card, Alert, Loader, Badge, Dialog } from '@dreamtree-org/twreact-ui';
import { GitCommitHorizontal, Trash2, RefreshCw, FileDiff } from 'lucide-react';
import Can from '@rbac/Can';
import { useGitApp, errText } from './useGitApp';

const OP_VARIANT = { write: 'primary', delete: 'error' };

/**
 * Staged, uncommitted changes per work branch.
 *
 * These rows outlive the clone they were made in — a workspace is deleted as soon as the
 * operation that created it ends, so without this store there would be nothing to show.
 * Committing re-clones the base branch, replays the staged edits onto the work branch and
 * pushes; it deliberately does NOT open a PR, which is a separate decision.
 */
export default function SourceControl({ appId }) {
  const { act } = useGitApp(appId);

  const [branches, setBranches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');

  const loadBranches = useCallback(async () => {
    setLoading(true);
    try {
      const d = await act('stagedBranches');
      setBranches(d.branches || []);
      setErr('');
    } catch (ex) {
      setErr(errText(ex, 'Could not load staged branches'));
    } finally {
      setLoading(false);
    }
  }, [act]);

  useEffect(() => { loadBranches(); }, [loadBranches]);

  const openBranch = useCallback(async (workBranch) => {
    setSelected(workBranch);
    setLoadingDetail(true);
    setErr('');
    try {
      setDetail(await act('stagedChanges', { workBranch }));
    } catch (ex) {
      setErr(errText(ex, 'Could not load staged changes'));
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, [act]);

  const commit = async () => {
    if (!selected) return;
    setBusy(true);
    setErr('');
    try {
      const r = await act('commitStaged', { workBranch: selected, ...(message.trim() ? { message: message.trim() } : {}) });
      setNotice(`Committed ${r.sha?.slice(0, 7)} to ${r.branch}.`);
      setMessage('');
      setDetail(null);
      setSelected(null);
      await loadBranches();
    } catch (ex) {
      setErr(errText(ex, 'Commit failed'));
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    setBusy(true);
    setErr('');
    try {
      const r = await act('discardStaged', { workBranch: selected });
      setNotice(`Discarded ${r.discarded} change(s).`);
      setConfirmDiscard(false);
      setDetail(null);
      setSelected(null);
      await loadBranches();
    } catch (ex) {
      setErr(errText(ex, 'Could not discard'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loader text="Loading staged changes…" />;

  return (
    <div className="space-y-4">
      {err ? <Alert variant="error" title="Source control">{err}</Alert> : null}
      {notice ? <Alert variant="success" title="Source control">{notice}</Alert> : null}

      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Work branches with pending changes</h2>
          <button type="button" className="opacity-50 hover:opacity-100" onClick={loadBranches} title="Refresh">
            <RefreshCw size={15} />
          </button>
        </div>

        {branches.length === 0 ? (
          <p className="text-sm opacity-50">Nothing staged. Edits made through the workspace appear here before they are committed.</p>
        ) : (
          <div className="divide-y divide-secondary-100 dark:divide-secondary-800">
            {branches.map((b) => (
              <button
                key={b.work_branch}
                type="button"
                onClick={() => openBranch(b.work_branch)}
                className={`w-full flex items-center gap-3 py-2 px-2 -mx-2 rounded text-left hover:bg-secondary-50 dark:hover:bg-secondary-800 ${selected === b.work_branch ? 'bg-primary-50' : ''}`}
              >
                <FileDiff size={14} className="opacity-50" />
                <span className="font-mono text-sm">{b.work_branch}</span>
                {b.base_branch ? <span className="text-[11px] opacity-50">← {b.base_branch}</span> : null}
                <Badge variant="primary" className="ml-auto">{b.files} file(s)</Badge>
              </button>
            ))}
          </div>
        )}
      </Card>

      {loadingDetail && <Loader text="Loading changes…" />}

      {!loadingDetail && detail ? (
        <Card className="p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-sm font-semibold font-mono">{detail.workBranch}</h2>
              <p className="text-[11px] opacity-60">
                {detail.baseBranch ? `based on ${detail.baseBranch}` : 'base branch not recorded'} · {detail.changes.length} change(s)
              </p>
            </div>
            <div className="flex gap-2">
              <Can module="development" action="update">
                <Button variant="destructive" size="sm" leftIcon={<Trash2 size={14} />} onClick={() => setConfirmDiscard(true)}>Discard</Button>
              </Can>
              <Can module="development" action="commit">
                <Button size="sm" leftIcon={<GitCommitHorizontal size={14} />} onClick={commit} loading={busy} disabled={!detail.changes.length}>
                  Commit to branch
                </Button>
              </Can>
            </div>
          </div>

          <Input
            label="Commit message (optional)"
            placeholder={`feat: ${detail.workBranch.replace(/-/g, ' ')}`}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />

          <div className="divide-y divide-secondary-100 dark:divide-secondary-800 max-h-[50vh] overflow-y-auto">
            {detail.changes.map((c) => (
              <div key={c.path} className="py-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={OP_VARIANT[c.op] || 'default'}>{c.op}</Badge>
                  <span className="font-mono text-xs break-all">{c.path}</span>
                  {c.origin ? <span className="text-[11px] opacity-50">via {c.origin}</span> : null}
                </div>
                {c.op !== 'delete' && c.content ? (
                  <pre className="mt-1 text-[11px] font-mono bg-secondary-50 dark:bg-secondary-800 rounded p-2 overflow-auto max-h-48 whitespace-pre">
                    {c.content.slice(0, 4000)}
                  </pre>
                ) : null}
              </div>
            ))}
          </div>

          <p className="text-[11px] opacity-60">
            Committing pushes this work branch. It does not open a pull request — use the Diff tab for that.
          </p>
        </Card>
      ) : null}

      {confirmDiscard ? (
        <Dialog isOpen onClose={() => setConfirmDiscard(false)} title={`Discard changes on ${selected}?`}>
          <div className="space-y-4">
            <p className="text-sm">
              The staged edits for this work branch are dropped. Nothing on GitHub is touched — these
              changes were never pushed.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirmDiscard(false)}>Cancel</Button>
              <Button variant="destructive" onClick={discard} loading={busy}>Discard</Button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
